// Converts Redux boxes/animations/soundList → IdeaTimeline layer objects.
import i18n from "i18next";
import { resolveBoxTiming, resolveBoxEndFrame } from "../../../../../common/resolveBoxTiming.es6";
import { classifyPluginTrackType } from "../../../../../common/media_track_types.es6";
import { isYoutubeUrl, fetchYoutubeDuration } from "../../../../../common/youtube_tools.es6";
import {
  SMART_ELEMENT_CONFIGS,
  decodeAtomicText,
} from "../../../../../plugins/AtomicBoxPlugin/atomic_box_plugin_config";
import { extractAnimations } from "../../../../../plugins/AtomicBoxPlugin/animations/extractAnimations";
import { humanizeFieldPath } from "./humanizeFieldPath";
import { audioCueManual } from "../../../../../common/audio_cue_manual.es6";

function getAtomicElementData(state) {
  const parsed = decodeAtomicText(state?.__text);
  return parsed?.elementData ?? (parsed?.type ? parsed : null);
}

function getSmartElementType(state) {
  return getAtomicElementData(state)?.type || null;
}

// One bar per sub-field, mirroring TrackClip:
//   bar.startFrame = parent.startFrame + entry.delay * fps   (parent.startFrame if no entry)
//   bar.endFrame   = parent.startFrame + (exit.delay + exit.duration) * fps
//                                                            (parent.endFrame if no exit)
// Entry segment pinned LEFT, exit segment pinned RIGHT.
function buildAtomicChildLayers(elementData, parentStartFrame, parentEndFrame, fps) {
  if (!elementData || typeof elementData !== "object") return [];
  const { animations } = extractAnimations(elementData);
  return animations.map(({ path, label, kind, entry, exit }) => {
    const entryDurationFrames = entry ? Math.max(1, Math.ceil(entry.duration * fps)) : 0;
    const exitDurationFrames = exit ? Math.max(1, Math.ceil(exit.duration * fps)) : 0;
    let startFrame = entry
      ? parentStartFrame + Math.round((entry.delay || 0) * fps)
      : parentStartFrame;
    let endFrame = exit
      ? parentStartFrame + Math.ceil(((exit.delay || 0) + exit.duration) * fps)
      : parentEndFrame;
    // Keep sub-fields inside the parent bounds — a field animation can't live
    // outside its element. Non-destructive: only the displayed frames are
    // clamped; the stored entry/exit delays stay intact, so growing the parent
    // back restores them.
    startFrame = Math.max(parentStartFrame, Math.min(startFrame, parentEndFrame));
    endFrame = Math.max(startFrame, Math.min(endFrame, parentEndFrame));
    return {
      path,
      label: label || humanizeFieldPath(path),
      kind,
      startFrame,
      endFrame,
      entryDurationFrames,
      exitDurationFrames,
      entryName: entry?.name ?? null,
      exitName: exit?.name ?? null,
    };
  });
}

const FALLBACK_NAMES = {
  Text: "Metin",
  TextPlayer: "Metin",
  Title: "Başlık",
  Image: "Görsel",
  Photo: "Fotoğraf",
  Video: "Video",
  VideoPlayer: "Video",
  Audio: "Ses",
  Sound: "Ses",
  Shapes: "Şekil",
  Shape: "Şekil",
  Ordering: "Sıralama",
  TrueFalse: "Doğru/Yanlış",
  AudioCue: "Ses İşareti",
  DragDrop: "Sürükle & Bırak",
  Matching: "Eşleştirme",
  OneClick: "Buton",
  AnimationOptions: "Animasyon",
  Characters: "Karakter",
  FillInBlank: "Boşluk Doldur",
};

export function getLayerType(box, pluginToolbars = {}) {
  const toolbar = pluginToolbars[box.id];
  const pluginId = toolbar?.pluginId || box.pluginId;
  if (!pluginId) return "element";
  const p = pluginId.toLowerCase();
  if (p === "scene-audio") return "scene-audio";
  // Media types come from the shared classifier so the timeline and
  // addBoxThunk's shared-row placement can never disagree. Its internal check
  // order (image → text-bailout → video → audio) preserves this function's
  // original precedence, e.g. "TextPlayer" stays text, not video.
  const media = classifyPluginTrackType(pluginId);
  if (media) return media;
  if (p.includes("text") || p.includes("title")) return "text";
  if (p.includes("shape")) return "shape";
  return "element";
}

export function getPluginDisplayName(pluginId) {
  if (!pluginId) return "Element";
  // Each plugin ships a localized "<Id>.PluginName" key in its own namespaced
  // locale (same key PluginRibbon uses) — prefer it so names like HotspotImages,
  // EnrichedPlayer, EnrichedPDF render translated in all languages.
  const translated = i18n.t(pluginId + ".PluginName", { defaultValue: "" });
  if (translated) return translated;
  const key = Object.keys(FALLBACK_NAMES).find((k) => k.toLowerCase() === pluginId.toLowerCase());
  return key ? FALLBACK_NAMES[key] : pluginId;
}

export function getThumbUrl(box, pluginToolbars) {
  const toolbar = pluginToolbars[box.id];
  return toolbar?.state?.url || box.url || box.src || null;
}

// Media sürelerini cache'lemek için - aynı URL için tekrar yükleme yapma
const _mediaDurationCache = new Map();
// Cache'e yeni bir URL eklendiğinde tetiklenecek listener'lar
const _cacheListeners = new Set();

export function onMediaDurationCached(cb) {
  _cacheListeners.add(cb);
  return () => _cacheListeners.delete(cb);
}

// External publish point: the editor canvas player (ReactPlayer onDuration)
// knows durations the probes here may not resolve (e.g. a failed YouTube
// lookup). Notifies listeners on any value change so a cached null heals.
export function setMediaDuration(url, duration) {
  if (!url || !Number.isFinite(duration) || duration <= 0) return;
  if (_mediaDurationCache.get(url) === duration) return;
  _mediaDurationCache.set(url, duration);
  _cacheListeners.forEach((cb) => cb(url, duration));
}

// MediaRecorder WebM/Opus (screen recordings, voice notes) ships no Duration
// header: the element reports Infinity until it has been seeked to the end
// once. Probe elements are throwaway, so force that seek and read it back.
function resolveInfiniteDuration(el, done) {
  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    el.removeEventListener("durationchange", onChange);
    el.removeEventListener("seeked", finish);
    clearTimeout(timer);
    const d = el.duration;
    done(Number.isFinite(d) && d > 0 ? d : null);
  };
  const onChange = () => {
    if (Number.isFinite(el.duration)) finish();
  };
  const timer = setTimeout(finish, 5000);
  el.addEventListener("durationchange", onChange);
  el.addEventListener("seeked", finish);
  try {
    el.currentTime = 1e101;
  } catch (_) {
    finish();
  }
}

// Media dosyasının süresini almak için yardımcı fonksiyon.
// Exported so the legacy-timeline repair (_editor/utils/mediaTimelineRepair)
// probes through the same cache the timeline already warms.
export async function getMediaDuration(url, type) {
  if (_mediaDurationCache.has(url)) {
    return _mediaDurationCache.get(url);
  }

  return new Promise((resolve) => {
    const onLoad = (duration) => {
      const isNew = !_mediaDurationCache.has(url);
      _mediaDurationCache.set(url, duration);
      if (isNew) {
        // Yeni bir süre yüklendi - dinleyicileri bildir
        _cacheListeners.forEach((cb) => cb(url, duration));
      }
      resolve(duration);
    };

    // YouTube boxes store an embed URL — not a media file, a <video> probe
    // errors on it. Resolve the duration from the Data API instead.
    if (type === "video" && isYoutubeUrl(url)) {
      fetchYoutubeDuration(url).then(onLoad);
      return;
    }

    if (type === "audio") {
      const audio = new Audio();
      audio.addEventListener("loadedmetadata", () => {
        const d = audio.duration;
        if (d === Infinity) {
          resolveInfiniteDuration(audio, onLoad);
          return;
        }
        if (!Number.isFinite(d) || d <= 0) {
          console.warn("[getMediaDuration] audio duration is not finite (missing metadata)", {
            url,
            duration: d,
          });
          onLoad(null);
          return;
        }
        onLoad(d);
      });
      // Not cached: a transient failure (e.g. 401 before the asset grant) must re-probe.
      audio.addEventListener("error", () => resolve(null));
      audio.src = url;
    } else if (type === "video") {
      const video = document.createElement("video");
      video.addEventListener("loadedmetadata", () => {
        const d = video.duration;
        if (d === Infinity) {
          resolveInfiniteDuration(video, onLoad);
          return;
        }
        if (!Number.isFinite(d) || d <= 0) {
          console.warn("[getMediaDuration] video duration is not finite (missing metadata)", {
            url,
            duration: d,
          });
          onLoad(null);
          return;
        }
        onLoad(d);
      });
      video.addEventListener("error", () => resolve(null));
      video.src = url;
    } else {
      resolve(null);
    }
  });
}

// Rich-text plugins store HTML, so a preview has to read like the text the
// author actually typed. Stripping tags alone is not enough on either count:
// a line break fuses the two lines into one word ("line1line2"), and the
// `&nbsp;` a paragraph break leaves behind used to show up verbatim on the
// timeline clip. Entities are decoded LAST, so an escaped `&lt;b&gt;` survives
// instead of being stripped as if it were a tag.
const NAMED_ENTITIES = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: "\u00a0",
};

export function htmlToPlainText(html) {
  if (typeof html !== "string") return "";
  return (
    html
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)>/gi, " ")
      .replace(/<[^>]*>/g, "")
      .replace(/&(#\d+|#x[0-9a-f]+|[a-z][a-z0-9]*);/gi, (match, code) => {
        if (code[0] === "#") {
          const cp =
            code[1] === "x" || code[1] === "X"
              ? parseInt(code.slice(2), 16)
              : parseInt(code.slice(1), 10);
          if (!Number.isFinite(cp) || cp <= 0 || cp > 0x10ffff) return match;
          return String.fromCodePoint(cp);
        }
        const named = NAMED_ENTITIES[code.toLowerCase()];
        return named === undefined ? match : named;
      })
      // \s covers the NBSP the decode above may have produced.
      .replace(/\s+/g, " ")
      .trim()
  );
}

function getContentPreview(box, pluginToolbars) {
  try {
    const toolbar = pluginToolbars[box.id];
    const state = toolbar?.state;
    const content = box.content || {};

    if (state?.__text && typeof state.__text === "string") {
      let t = state.__text;
      try {
        t = decodeURIComponent(t);
      } catch {}
      t = htmlToPlainText(t);
      if (t) return t.length > 30 ? t.slice(0, 30) + "…" : t;
    }

    if (content.title && typeof content.title === "string") {
      const title = htmlToPlainText(content.title);
      if (title) return title.length > 30 ? title.slice(0, 30) + "…" : title;
    }

    if (state?.question && typeof state.question === "string") {
      const q = htmlToPlainText(state.question);
      if (q) return q.length > 25 ? q.slice(0, 25) + "…" : q;
    }
  } catch {}
  return null;
}

// "Canlandır" çıktısı: yerinde webp/gif'e dönen HotspotImages (EditorApp
// updateAtomicLayerCurrentTime ile aynı regex) ya da mp4 box-swap'ı ile
// originalImageUrl taşıyan EnrichedPlayer (applyAnimationResult.es6).
function isAnimatedImageBox(toolbar) {
  if (!toolbar) return false;
  if (toolbar.pluginId === "HotspotImages") {
    const url = toolbar.state?.url;
    return typeof url === "string" && /(\.webp|\.gif|\.apng|\.avif)(\?|#|$)/i.test(url);
  }
  return toolbar.pluginId === "EnrichedPlayer" && !!toolbar.state?.originalImageUrl;
}

export function getLayerName(box, pluginToolbars) {
  if (box.title?.trim()) return box.title.trim();

  const toolbar = pluginToolbars[box.id];
  const pluginId = toolbar?.pluginId || box.pluginId || "";

  // Smart elements (AtomicBoxPlugin) store their content as URI-encoded JSON in state.__text.
  if (pluginId.toLowerCase() === "atomicboxplugin") {
    const type = getSmartElementType(toolbar?.state);
    if (type) {
      const cfg = SMART_ELEMENT_CONFIGS.find((c) => c.type === type);
      return i18n.t(`AtomicBoxPlugin.element_types.${type}`, {
        defaultValue: cfg?.displayName || type,
      });
    }
    // __text is JSON, not prose — never surface it as a content preview.
    return getPluginDisplayName(pluginId);
  }

  const preview = getContentPreview(box, pluginToolbars);
  if (preview) return preview;

  if (isAnimatedImageBox(toolbar)) {
    return i18n.t("TimelineAnimatedImage", { defaultValue: "Video Görsel" });
  }

  return getPluginDisplayName(pluginId);
}

/**
 * buildLayersFromBoxes
 *
 * @param {object} boxes          - boxesById from Redux
 * @param {object} animations     - animationsById from Redux
 * @param {string} currentScene   - active scene/page ID
 * @param {object} navItemsById   - navItemsById from Redux
 * @param {object} soundList      - soundList from Redux
 * @param {object} pluginToolbars - pluginToolbarsById from Redux
 * @param {number} fps
 * @param {number} defaultDuration - accepted for compatibility; element spans
 *   no longer stretch to it (duration marker is decoupled from elements)
 */
export async function buildLayersFromBoxes({
  boxes = {},
  animations = {},
  currentScene,
  navItemsById = {},
  soundList = {},
  pluginToolbars = {},
  fps = 30,
  defaultDuration = 1800, // eslint-disable-line no-unused-vars
}) {
  // ── Filter boxes that belong to current scene ────────────
  const boxIds = Object.keys(boxes).filter((id) => {
    const box = boxes[id];
    if (!box || typeof box !== "object") return false;

    if (currentScene) {
      const scene = navItemsById[currentScene];
      return (
        scene?.boxes?.includes(id) || box.parent === currentScene || box.container === currentScene
      );
    }
    return !id.includes("_");
  });

  // ── Ön geçiş: sahne max endFrame ─────────────────────────
  const boxMaxEndFrame = boxIds.reduce((max, id) => {
    const ef = boxes[id]?.timeline?.endFrame;
    return typeof ef === "number" && ef > max ? ef : max;
  }, 0);

  // soundList'ten scene audio bitiş zamanını önceden hesapla (senkron + cache)
  const sceneSoundsPreview = currentScene && soundList[currentScene] ? soundList[currentScene] : [];
  const soundMaxEndFrame = sceneSoundsPreview.reduce((max, sound) => {
    // loopScene sounds tile across the scene instead of extending it — see
    // common/sceneTimeline.es6.
    if (!sound || sound.loopScene) return max;
    const startSec = parseFloat(sound.startTime || 0);
    const rawEndSec = parseFloat(sound.endTime || 0);
    const dur = parseFloat(sound.duration || 0);
    if (rawEndSec > startSec) return Math.max(max, Math.floor(rawEndSec * fps));
    if (dur > 0) return Math.max(max, Math.floor((startSec + dur) * fps));
    // endTime/duration yoksa cache'e bak (async yükleme tamamlandıysa buradan gelir)
    const audioUrl = sound.file || sound.url || sound.src;
    if (audioUrl && _mediaDurationCache.has(audioUrl)) {
      const cachedDur = _mediaDurationCache.get(audioUrl);
      if (typeof cachedDur === "number" && cachedDur > 0)
        return Math.max(max, Math.floor((startSec + cachedDur) * fps));
    }
    return max;
  }, 0);

  // Tüm elementlerin efektif startFrame'ini önceden hesapla (delay dahil)
  // → sahne sesinden sonra başlayan element varsa sceneMaxEndFrame bunu kapsamalı
  const maxElementStartFrame = boxIds.reduce((max, id) => {
    const box = boxes[id];
    if (!box) return max;
    const tl = box.timeline || {};
    let sf = typeof tl.startFrame === "number" ? Math.max(0, tl.startFrame) : 0;
    const delay = box.boxAnimation?.delay ?? box.textAnimation?.delay ?? null;
    if (typeof delay === "number" && delay > 0) sf = Math.round(delay * fps);
    return Math.max(max, sf);
  }, 0);

  // boxAnimation/textAnimation bitiş zamanını hesapla: startFrame + duration*fps
  // → explicit timeline.endFrame olmayan animasyonlu kutular için sahne süresini belirler
  const maxAnimationEndFrame = boxIds.reduce((max, id) => {
    const box = boxes[id];
    if (!box) return max;
    const anim = box.boxAnimation ?? (box.textAnimation?.animation ? box.textAnimation : null);
    if (!anim?.animation) return max;
    const tl = box.timeline || {};
    if (typeof tl.endFrame === "number") return max; // kullanıcı set ettiyse zaten boxMaxEndFrame'de
    const userMovedStart = typeof tl.startFrame === "number" && tl.startFrame > 0;
    const delay = anim.delay ?? 0;
    const sf = userMovedStart
      ? Math.max(0, tl.startFrame)
      : delay > 0
        ? Math.round(delay * fps)
        : 0;
    const dur = typeof anim.duration === "number" ? anim.duration : 1;
    return Math.max(max, sf + Math.ceil(dur * fps));
  }, 0);

  // Öncelik: explicit box endFrames > animasyon bitiş > scene audio sonu.
  // Ayrıca geç başlayan elementleri de kapsayacak şekilde büyüt.
  // The it-duration-marker (defaultDuration) is deliberately NOT part of this:
  // new boxes get a fixed timeline.endFrame stamped at insertion (addBoxThunk),
  // so the marker never retro-affects element lifetimes. Legacy endFrame-less
  // boxes stretch to the CONTENT end only.
  const sceneMaxEndFrame = Math.max(
    boxMaxEndFrame,
    maxAnimationEndFrame,
    soundMaxEndFrame > 0 ? soundMaxEndFrame : 0,
    // Sahne sesinden sonra başlayan element varsa, ses sonunu en az o kadar uzat
    soundMaxEndFrame > 0 && maxElementStartFrame > soundMaxEndFrame ? maxElementStartFrame + fps : 0
  );

  // ── Build layer objects ───────────────────────────────────
  // Real stacking order — same array the canvas paints from (later id = on
  // top, see EditorCanvasDoc) and the same one the canvas "bring to
  // front/back" buttons mutate via changeBoxLayer. Falls back to legacy
  // box.layer / insertion index for boxes not present in it (shouldn't
  // normally happen for a valid scene).
  const sceneBoxOrder = navItemsById[currentScene]?.boxes;
  const orderIndexOf = (id) => (Array.isArray(sceneBoxOrder) ? sceneBoxOrder.indexOf(id) : -1);

  const layers = await Promise.all(
    boxIds.map(async (id, index) => {
      const rawBox = boxes[id];
      const { textAnimation, boxAnimation, boxExitAnimation, ...box } = rawBox;
      let boxAnimations = Array.isArray(animations[id]) ? animations[id] : [];

      const layerType = getLayerType(box, pluginToolbars);
      const mediaUrl = getThumbUrl(box, pluginToolbars);

      // ── Canonical timing (shared with VisorCanvasSli + VisorBox) ────
      const { startFrame: resolvedStart, endFrame: resolvedEnd } = resolveBoxTiming(
        rawBox,
        boxAnimations,
        fps
      );

      let startFrame = resolvedStart ?? 0;
      let endFrame = resolvedEnd ?? null;

      boxAnimations = []; // legacy anims consumed by resolveBoxTiming

      // textAnimation → boxAnimation sistemine dahil et (boxAnimation yoksa)
      const resolvedBoxAnimation =
        boxAnimation ?? (textAnimation?.animation ? textAnimation : null);

      // ── Video/Audio süresi otomatik hesaplama ────────────────
      // Never BLOCK the build on media metadata: use the cache when warm,
      // otherwise kick the probe off in the background — it notifies
      // onMediaDurationCached listeners, which re-run the build. The first
      // paint uses the persisted endFrame (or the fallback below) meanwhile.
      if (mediaUrl && (layerType === "audio" || layerType === "video")) {
        let mediaDuration = null;
        if (_mediaDurationCache.has(mediaUrl)) {
          mediaDuration = _mediaDurationCache.get(mediaUrl);
        } else {
          getMediaDuration(mediaUrl, layerType);
        }
        if (
          endFrame === null &&
          typeof mediaDuration === "number" &&
          Number.isFinite(mediaDuration) &&
          mediaDuration > 0
        ) {
          // Shared own-end rule (also used by the visor) — same frame on both sides.
          endFrame = resolveBoxEndFrame(rawBox, Math.ceil(mediaDuration * fps), boxAnimations, fps);
        }
      }

      if (endFrame === null) {
        // Legacy box with no persisted endFrame: content end, floored at a
        // fixed 5s — never the duration marker.
        endFrame = Math.max(startFrame + 150, sceneMaxEndFrame);
      }

      const hasAnimations = boxAnimations.length > 0;

      let childLayers;
      const toolbar = pluginToolbars[id];
      if (toolbar?.pluginId === "AtomicBoxPlugin") {
        const elementData = getAtomicElementData(toolbar.state);
        const children = buildAtomicChildLayers(elementData, startFrame, endFrame, fps);
        if (children.length > 0) childLayers = children;
      }

      return {
        id,
        name: getLayerName(box, pluginToolbars),
        thumbUrl: getThumbUrl(box, pluginToolbars),
        // Source URL — the timeline needs it to tell an iframe-backed player
        // (YouTube) apart from a real <video> it can drive through the DOM.
        mediaUrl,
        startFrame,
        endFrame,
        trimStart: typeof box.timeline?.trimStart === "number" ? box.timeline.trimStart : 0,
        trimEnd: typeof box.timeline?.trimEnd === "number" ? box.timeline.trimEnd : null,
        // Shared by every piece a clip was cut into (see TrackClip split) —
        // lets the timeline visually link split siblings back together.
        splitGroup: typeof box.timeline?.splitGroup === "string" ? box.timeline.splitGroup : null,
        // Persisted shared-row membership; null = own row (see groupRows.js).
        trackId: typeof box.timeline?.trackId === "string" ? box.timeline.trackId : null,
        // Scene frame where autoPlay playback begins (defaults to the clip start).
        playStartFrame:
          typeof box.timeline?.playStartFrame === "number"
            ? box.timeline.playStartFrame
            : startFrame,
        // autoPlay drives the inline play-window bar (video only).
        autoPlay: toolbar?.state?.autoPlay === true,
        // A manual AudioCue is the learner's: the preview does not start it.
        manualCue: toolbar?.pluginId === "AudioCue" && audioCueManual(toolbar.state, box),
        // loop drives the timeline clip's loop toggle (video only).
        loop: toolbar?.state?.loop === true,
        // Set on a video the Video Director rendered (see openInDirector).
        videoProjectId:
          typeof toolbar?.state?.videoProjectId === "string" && toolbar.state.videoProjectId
            ? toolbar.state.videoProjectId
            : null,
        mediaDurationFrames:
          _mediaDurationCache.get(mediaUrl) > 0
            ? Math.ceil(_mediaDurationCache.get(mediaUrl) * fps)
            : null,
        visible:
          (box.timeline?.visible !== undefined ? box.timeline.visible : box.visible) !== false,
        locked: box.timeline?.locked === true || box.locked === true,
        zIndex: (() => {
          const sceneOrder = orderIndexOf(id);
          if (sceneOrder !== -1) return sceneOrder;
          return typeof box.layer === "number" ? box.layer : index;
        })(),
        type: getLayerType(box, pluginToolbars),
        box,
        animations: boxAnimations,
        hasAnimations,
        canEdit: !box.locked,
        boxAnimation: resolvedBoxAnimation,
        boxExitAnimation: boxExitAnimation ?? null,
        childLayers,
      };
    })
  );

  // ── Add scene audio layers (tümünü ekle) ─────────────────
  const sceneSounds = currentScene && soundList[currentScene] ? soundList[currentScene] : [];
  await Promise.all(
    sceneSounds.map(async (sound, idx) => {
      if (!sound) return;
      const startSec = parseFloat(sound.startTime || 0);
      let audioDuration = parseFloat(sound.duration || 0);
      const rawEndSec = parseFloat(sound.endTime || 0);

      // Gerçek medya süresi — cache'ten senkron; soğuksa arka planda (listener
      // rebuild'i tetikler), bloklamaz. Hem endTime/duration yokken klip
      // uzunluğu için, hem de kenar sürüklemesini dosyanın sonunda durdurmak
      // için (mediaDurationFrames, bkz. utils/sourceBounds) gerekli.
      const audioUrl = sound.file || sound.url || sound.src;
      let mediaDuration = null;
      if (audioUrl) {
        if (_mediaDurationCache.has(audioUrl)) {
          mediaDuration = _mediaDurationCache.get(audioUrl);
        } else {
          getMediaDuration(audioUrl, "audio");
        }
      }
      if (rawEndSec <= startSec && audioDuration <= 0 && mediaDuration > 0) {
        audioDuration = mediaDuration;
      }

      const endSec =
        rawEndSec > startSec
          ? rawEndSec
          : audioDuration > 0
            ? startSec + audioDuration
            : startSec + 30;
      layers.push({
        id: sound.id || `scene-audio-${idx}`,
        name: sound.name || sound.title || `Scene Audio ${idx + 1}`,
        startFrame: Math.max(0, Math.floor(startSec * fps)),
        // Real length: a 1s floor drew short split pieces over their neighbour
        // and a re-split took that inflated end as the piece's own.
        endFrame: Math.max(Math.floor(startSec * fps) + 1, Math.floor(endSec * fps)),
        visible: sound.visible !== false,
        locked: sound.locked === true,
        zIndex: -1,
        type: "scene-audio",
        box: { pluginId: "scene-audio" },
        animations: [],
        hasAnimations: false,
        canEdit: true,
        audioData: sound,
        soundIndex: idx,
        mediaDurationFrames: mediaDuration > 0 ? Math.ceil(mediaDuration * fps) : null,
        // Same visual split-linking as box clips (amber ring on group hover).
        splitGroup: typeof sound.splitGroup === "string" ? sound.splitGroup : null,
        // User-chosen Timeline row position (see setSceneAudioRowOrders) —
        // shares the SAME numeric axis as regular layers' zIndex (fractional
        // values slot a narration row between two specific boxes), so it can
        // be freely interleaved with any other row, not just other narration
        // rows. Distinct from soundIndex, which just mirrors the soundList
        // array's own storage order and is always chronological (startTime).
        // null until the user has manually dragged this row at least once.
        rowOrder: typeof sound.rowOrder === "number" ? sound.rowOrder : null,
        // Shared-row membership for narrations — same concept as
        // box.timeline.trackId, persisted on the sound object instead.
        trackId: typeof sound.trackId === "string" ? sound.trackId : null,
        sceneId: currentScene,
        splitGroup: typeof sound.splitGroup === "string" ? sound.splitGroup : null,
      });
    })
  );

  // Box layers' zIndex IS their real index in scene.boxes — one shared,
  // globally-comparable number line across every box type, low = back.
  // Scene-audio rows aren't part of that array at all, so by default they sit
  // just BELOW its start (behind every box, tie-broken by soundIndex ==
  // chronological order — the array is always kept startTime-sorted), which
  // puts them at the bottom of the timeline once the caller flips the sorted
  // list for display (top row = frontmost). A manually dragged narration row
  // (rowOrder, see setSceneAudioRowOrders) is a FRACTIONAL value on that same
  // axis — e.g. 2.5 to sit between the boxes at zIndex 2 and 3 — which is what
  // lets it slot in anywhere among the other rows.
  const minBoxZIndex = layers.reduce(
    (m, l) =>
      l.type !== "scene-audio" && typeof l.zIndex === "number" ? Math.min(m, l.zIndex) : m,
    Infinity
  );
  const sceneAudioBase = Number.isFinite(minBoxZIndex) ? minBoxZIndex : 0;
  const sceneAudioPos = (l) =>
    typeof l.rowOrder === "number" ? l.rowOrder : sceneAudioBase - 1 - (l.soundIndex ?? 0) / 1e6;

  // Narrations (scene-audio) are PINNED to the bottom of the timeline: they
  // have no canvas presence, so they must never sit above a visible element's
  // row — rowOrder only orders them among themselves. Clickable/visible sound
  // BOXES (AudioCue, audio players) are ordinary canvas elements and sort
  // freely by their real z-order like everything else.
  layers.sort((a, b) => {
    const aIsSceneAudio = a.type === "scene-audio";
    const bIsSceneAudio = b.type === "scene-audio";

    if (aIsSceneAudio && !bIsSceneAudio) return -1; // narration below every box row
    if (!aIsSceneAudio && bIsSceneAudio) return 1;
    if (aIsSceneAudio && bIsSceneAudio) return sceneAudioPos(a) - sceneAudioPos(b);

    return a.zIndex - b.zIndex;
  });
  return layers;
}
